import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { MainCalendarService, Sector, District } from '../../services/main-calendar.service';
import { AuthService } from '../../../../auth/auth.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-sector-calendar',
  templateUrl: './sector-calendar.component.html',
  styleUrls: ['./sector-calendar.component.scss'],
  standalone: false,
})
export class SectorCalendarComponent implements OnInit {
  public moment = require('moment');
  public currentMonthName: string;
  public nextMonthName: string;
  public currentMonthDays: any[] = [];
  public nextMonthDays: any[] = [];
  public weekDays = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];
  public sectors: Sector[] = [];
  public selectedSector: Sector | null = null;
  public districts: District[] = [];
  public selectedDistrict: District | null = null;
  public loadingDistricts = false;
  public loading = false;
  public loadingHeatMap = false;
  public selectedDate: Date | null = null;
  public dayStatistics: any = null;
  public dayWorkload: any = null; // Данные о загруженности выбранного дня
  public loadingStatistics = false;
  private clickTimeout: any;
  private clickCount = 0;
  private heatMapData: {
    [key: string]: {
      workload: number;
      totalHours: number;
      busyHours: number;
    };
  } = {}; // Храним загруженность по датам

  constructor(
    private mainCalendarService: MainCalendarService,
    private authService: AuthService,
    private router: Router
  ) {}

  ngOnInit() {
    this.moment.locale('ru');
    this.loadSectors();
  }

  private loadSectors() {
    const userId = this.authService.getAuthKey('u_id');
    if (!userId) return;

    this.loading = true;
    this.mainCalendarService.getUserSectors(userId).subscribe(
      (response) => {
        this.sectors = response.data || [];
        if (this.sectors.length > 0) {
          this.selectedSector = this.sectors[0];
          this.selectedDate = new Date(); // По умолчанию текущий день
          this.loadDistricts(); // Загружаем районы для первого сектора
          this.loadHeatMapData();
        } else {
          this.generateCalendars();
        }
        this.loading = false;
      },
      (error) => {
        console.error('Error loading sectors:', error);
        this.loading = false;
        this.generateCalendars();
      }
    );
  }

  public onSelectSector(sector: Sector) {
    this.selectedSector = sector;
    this.selectedDistrict = null; // Сброс выбранного района при смене сектора
    this.districts = [];
    this.dayStatistics = null;
    this.dayWorkload = null;

    // Устанавливаем текущую дату по умолчанию
    if (!this.selectedDate) {
      this.selectedDate = new Date();
    }

    // Загружаем районы для выбранного сектора
    this.loadDistricts();
    this.loadHeatMapData();
  }

  private loadDistricts() {
    if (!this.selectedSector) return;

    this.loadingDistricts = true;
    this.mainCalendarService.getDistricts(this.selectedSector.sector_id).subscribe(
      (response) => {
        this.districts = response.data || [];
        this.loadingDistricts = false;
      },
      (error) => {
        console.error('Error loading districts:', error);
        this.districts = [];
        this.loadingDistricts = false;
      }
    );
  }

  public onSelectDistrict(district: District | null) {
    this.selectedDistrict = district;
    this.dayStatistics = null;
    this.dayWorkload = null;

    // Перезагружаем данные с учетом выбранного района
    this.loadHeatMapData();
  }

  public onDistrictChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const districtId = selectElement.value;

    if (districtId === '') {
      this.onSelectDistrict(null);
    } else {
      const district = this.districts.find((d) => d.d_id === parseInt(districtId, 10));
      this.onSelectDistrict(district || null);
    }
  }

  public onDayClick(day: any) {
    if (!day) return;

    this.clickCount++;

    if (this.clickCount === 1) {
      this.clickTimeout = setTimeout(() => {
        // Одинарный клик - показываем статистику
        this.selectedDate = day.date;
        this.updateDayWorkload(day.dateStr);
        this.loadDayStatistics(day.dateStr);
        this.clickCount = 0;
      }, 300);
    } else if (this.clickCount === 2) {
      // Двойной клик - открываем распределение заявок
      clearTimeout(this.clickTimeout);
      this.clickCount = 0;
      this.openTicketsAssign(day.dateStr);
    }
  }

  private openTicketsAssign(dateStr: string) {
    if (!this.selectedSector) return;

    // Форматируем дату в начало и конец дня для фильтра
    const intBegin = `${dateStr}T00:00:00`;
    const intEnd = `${dateStr}T23:59:59`;

    // Сохраняем параметры в sessionStorage под фиксированным ключом
    const paramsForNewWindow = {
      int_begin: intBegin,
      int_end: intEnd,
      districts_ids: '',
      address: '',
      tickets_types_ids: '1,2,3,4,5,6',
      brigades_ids: '',
      hd_ticket_number: '',
      ticket_status: '12,1,2,3,4,5,6,7,8,9,11,10',
      hd_ticket_status_group: '1,2,3,4,5,6,7,8',
      gp_types: '',
      gp_subtypes: '',
      responsible_groups: '',
      responsible_users: '',
      _timestamp: Date.now(), // Добавляем timestamp для проверки актуальности
    };

    // Используем фиксированный ключ
    const storageKey = 'tickets_assign_pending_params';
    sessionStorage.setItem(storageKey, JSON.stringify(paramsForNewWindow));

    // Формируем URL с матричными параметрами напрямую
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        `/work_shift/sector/${this.selectedSector.sector_id}/tickets-new`,
        {
          int_begin: intBegin,
          int_end: intEnd,
          districts_ids: '',
          address: '',
          tickets_types_ids: '1,2,3,4,5,6',
          brigades_ids: '',
          hd_ticket_number: '',
          ticket_status: '12,1,2,3,4,5,6,7,8,9,11,10',
          hd_ticket_status_group: '1,2,3,4,5,6,7,8',
          gp_types: '',
          gp_subtypes: '',
          responsible_groups: '',
          responsible_users: '',
        },
      ])
    );

    // Открываем в новом окне
    window.open(url, '_blank');
  }

  private updateDayWorkload(dateStr: string) {
    this.dayWorkload = this.heatMapData[dateStr] || null;
  }

  private loadDayStatistics(dateStr: string) {
    if (!this.selectedSector) return;

    this.loadingStatistics = true;

    // Если выбран район, используем API района, иначе API сектора
    const apiCall = this.selectedDistrict
      ? this.mainCalendarService.getDistrictNumberOfTickets(this.selectedDistrict.d_id, dateStr, dateStr)
      : this.mainCalendarService.getNumberOfTickets(this.selectedSector.sector_id, dateStr, dateStr);

    apiCall.subscribe(
      (response) => {
        // API возвращает массив days, берем первый элемент
        if (response.data && response.data.days && response.data.days.length > 0) {
          this.dayStatistics = response.data.days[0];
        } else {
          this.dayStatistics = null;
        }
        this.loadingStatistics = false;
      },
      (error) => {
        console.error('Error loading day statistics:', error);
        this.dayStatistics = null;
        this.loadingStatistics = false;
      }
    );
  }

  private loadHeatMapData() {
    if (!this.selectedSector) {
      this.generateCalendars();
      return;
    }

    const now = new Date();
    const startDate = this.moment(now).startOf('month').format('YYYY-MM-DD');

    const endOfNextMonth = this.moment(now).add(1, 'month').endOf('month');
    const endDate = endOfNextMonth.format('YYYY-MM-DD');

    this.loadingHeatMap = true;

    // Если выбран район, используем API района, иначе API сектора
    const apiCall = this.selectedDistrict
      ? this.mainCalendarService.getDistrictWorkingHeatMap(this.selectedDistrict.d_id, startDate, endDate)
      : this.mainCalendarService.getWorkingHeatMap(this.selectedSector.sector_id, startDate, endDate);

    apiCall.subscribe(
      (response) => {
        this.processHeatMapData(response.data.days);
        this.generateCalendars();
        this.loadingHeatMap = false;

        // После загрузки heatMap загружаем статистику для текущего дня
        if (this.selectedDate) {
          const dateStr = this.moment(this.selectedDate).format('YYYY-MM-DD');
          this.updateDayWorkload(dateStr);
          this.loadDayStatistics(dateStr);
        }
      },
      (error) => {
        console.error('Error loading heat map:', error);
        this.heatMapData = {};
        this.generateCalendars();
        this.loadingHeatMap = false;
      }
    );
  }

  private processHeatMapData(days: any[]) {
    this.heatMapData = {};

    if (!days || !Array.isArray(days)) {
      return;
    }

    // Преобразуем данные в объект { дата: { workload, totalHours, busyHours } }
    days.forEach((item: any) => {
      if (item.day) {
        const date = this.moment(item.day).format('YYYY-MM-DD');

        // workload_level может быть числом или объектом с parsedValue
        let workload = 0;
        if (typeof item.workload_level === 'number') {
          workload = item.workload_level;
        } else if (item.workload_level && typeof item.workload_level.parsedValue === 'number') {
          workload = item.workload_level.parsedValue;
        }

        this.heatMapData[date] = {
          workload: workload,
          totalHours: item.total_hours || 0,
          busyHours: item.busy_hours || 0,
        };
      }
    });
  }

  private generateCalendars() {
    const now = new Date();
    this.currentMonthName = this.moment(now).format('MMMM').charAt(0).toUpperCase() + this.moment(now).format('MMMM').slice(1);

    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    this.nextMonthName = this.moment(nextMonth).format('MMMM').charAt(0).toUpperCase() + this.moment(nextMonth).format('MMMM').slice(1);

    this.currentMonthDays = this.generateMonthDays(now);
    this.nextMonthDays = this.generateMonthDays(nextMonth);
  }

  private generateMonthDays(date: Date): any[] {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days: any[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Получаем день недели первого дня (0 = воскресенье, нужно сделать 1 = понедельник)
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    // Добавляем пустые ячейки для выравнивания
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(null);
    }

    // Заполняем дни месяца
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const currentDate = new Date(year, month, day);
      const dayOfWeek = currentDate.getDay();

      // Создаем строковую дату в формате YYYY-MM-DD для безопасной работы
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = this.heatMapData[dateStr];
      const workload = dayData ? dayData.workload : undefined;
      const hasData = !!dayData;

      days.push({
        date: currentDate,
        dateStr: dateStr, // Добавляем строковую дату
        dayNumber: day,
        isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
        isHoliday: this.checkIfHoliday(currentDate),
        isCurrent: currentDate.getTime() === today.getTime(),
        workload: workload !== undefined ? workload : 0,
        hasData: hasData,
        totalHours: dayData ? dayData.totalHours : 0,
        busyHours: dayData ? dayData.busyHours : 0,
      });
    }

    return days;
  }

  private checkIfHoliday(date: Date): boolean {
    const holidays = ['01-01', '01-07', '02-23', '03-08', '05-01', '05-09', '06-12', '11-04'];
    const monthDay = this.moment(date).format('MM-DD');
    return holidays.includes(monthDay);
  }

  public getDayClasses(day: any): string {
    if (!day) return 'calendar-day--empty';

    const classes = ['calendar-day'];

    if (day.isWeekend) classes.push('calendar-day--weekend');
    if (day.isCurrent) classes.push('calendar-day--current');

    // Добавляем класс для выбранного дня
    if (this.selectedDate && day.date) {
      const selectedDateStr = this.moment(this.selectedDate).format('YYYY-MM-DD');
      const dayDateStr = this.moment(day.date).format('YYYY-MM-DD');
      if (selectedDateStr === dayDateStr) {
        classes.push('calendar-day--selected');
      }
    }

    return classes.join(' ');
  }

  public getWorkloadClass(workload: number): string {
    if (workload >= 90) return 'workload-high';
    if (workload >= 50) return 'workload-medium';
    if (workload >= 0) return 'workload-low';
    return '';
  }

  // Геттеры для шапки календаря
  get formattedSelectedDate(): string {
    if (!this.selectedDate) {
      return this.moment(new Date()).format('DD.MM.YYYY');
    }
    return this.moment(this.selectedDate).format('DD.MM.YYYY');
  }

  get selectedDayWorkloadPercent(): number {
    if (!this.selectedDate) return 0;

    const dateStr = this.moment(this.selectedDate).format('YYYY-MM-DD');
    const dayData = this.heatMapData[dateStr];

    return dayData ? Math.round(dayData.workload) : 0;
  }

  get sectorsDisplayText(): string {
    if (!this.sectors || this.sectors.length === 0) return '';
    return this.sectors.map((s) => s.sector_number_str).join(' ');
  }

  public openTicketsView(): void {
    if (!this.selectedSector || !this.selectedDate) return;

    const dateStr = this.moment(this.selectedDate).format('YYYY-MM-DD');
    this.openTicketsAssign(dateStr);
  }
}
